/**
 * screenshotScanVariant — variant routing for the still-image upload path.
 *
 * The still-image "Scan Pokédex" upload (`ScanPokedexModal`) writes directly to
 * the user's Pokédex after an explicit confirm + save, so a wrong dex bucket
 * here is a HIGH-RISK false write — strictly worse than a dropped live frame.
 * This module makes the upload path resolve the active Dex variant with the SAME
 * authority order the live scan uses in `adaptDexLiveFrame`:
 *
 *   1. The PoGo bottom filter-pill ACTIVE-PILL TEXT (`frame.variantChip`) is the
 *      authoritative source — active pill text beats wash colour. This is the
 *      exact signal the live adapter honours first (chip.variant @ ≥0.6 conf).
 *   2. The colour-narrowed pill re-read (`frame.colourNarrowedVariant`), live's
 *      recovery when the active-pill anchor missed on a known page colour.
 *
 * When neither resolves a variant confidently we DO NOT silently inherit the
 * caller's currently-viewed Dex tab (`suggestedVariant`). Doing so is exactly
 * the bug this fixes: a user viewing the PURIFIED tab who uploads a LUCKY
 * screenshot had the leaked PURIFIED context written as the dex. Instead we mark
 * the frame `needsConfirmation` and surface the viewed tab only as a *suggestion*
 * the user must explicitly confirm before any write happens.
 *
 * Pure module: no DOM, no OCR engine imports, so it is unit-testable with tsx +
 * node:assert like the rest of client/src/lib.
 */

import type { VariantChipSignal } from "./pokedexOcr";
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

/**
 * Minimum chip-text/hue confidence for the active-pill read to be trusted.
 * MIRRORS `VARIANT_CHIP_MIN_CONFIDENCE` (0.6) in dexLiveFrameAdapter — the
 * upload path must apply the same bar as the live path. Below this the live
 * adapter drops the frame rather than defaulting; here we require confirmation.
 */
export const SCREENSHOT_VARIANT_MIN_CONFIDENCE = 0.6;

/**
 * The minimum subset of a `PokedexGridFrame` this resolver reads. Declared
 * structurally so callers (and tests) need not build a whole frame.
 */
export interface ScreenshotVariantFrame {
  variantChip?: VariantChipSignal;
  colourNarrowedVariant?: PokedexVariant | null;
}

export interface ScreenshotVariantDecision {
  /**
   * The confidently-resolved variant, or `null` when the active-pill read could
   * not pin one. A null variant MUST NOT be written — the caller blocks save and
   * requires the user to confirm a variant first.
   */
  variant: PokedexVariant | null;
  /** How the variant was resolved (diagnostics / UI labelling). */
  source: "chip-text" | "chip-hue" | "colour-narrowed" | "unresolved";
  /**
   * True when no confident variant was read. The caller must require explicit
   * user confirmation (a variant picker) before saving this frame.
   */
  needsConfirmation: boolean;
  /**
   * The caller's currently-viewed Dex tab, surfaced ONLY as a pre-fill
   * suggestion for the confirmation picker when `needsConfirmation` is true.
   * Never auto-applied. Null when the caller supplied no suggestion.
   */
  suggested: PokedexVariant | null;
}

/**
 * Resolve the Dex variant for one uploaded screenshot frame.
 *
 * @param frame             the extracted frame (only `variantChip` /
 *                          `colourNarrowedVariant` are read).
 * @param suggestedVariant  the caller's currently-viewed Dex tab. Used ONLY as a
 *                          confirmation-picker pre-fill when the read is
 *                          inconclusive — never as a silent fallback.
 */
export function resolveScreenshotScanVariant(
  frame: ScreenshotVariantFrame,
  suggestedVariant?: PokedexVariant | null,
): ScreenshotVariantDecision {
  const chip = frame.variantChip;

  // 1. Active-pill text is authoritative (text > wash colour), at the same
  //    confidence bar the live adapter applies.
  //
  //    EXCEPTION — geometry-failed flat-band reads (`chip.bandFallback`). When the
  //    active-pill GEOMETRY could not be located, `classifyActivePillBand` falls
  //    back to a single OCR over the WHOLE pill strip and first-match-wins picks a
  //    keyword. That read cannot tell which pill is *active*: on a Lucky/G-Max page
  //    the always-present leftmost "POKÉMON" pill is the most legible token, so the
  //    fallback can confidently return `pokemon` for a Lucky/G-Max screenshot —
  //    exactly the "handled as Pokémon (All)" false-write the user reported. The
  //    live path tolerates this (multi-frame voting recovers a bad frame); the
  //    still-image upload commits ONE frame after confirm, so we must NOT
  //    auto-trust it. Fall through to require explicit user confirmation instead.
  if (
    chip &&
    chip.variant != null &&
    chip.confidence >= SCREENSHOT_VARIANT_MIN_CONFIDENCE &&
    !chip.bandFallback
  ) {
    return {
      variant: chip.variant,
      source: chip.source === "chip-text" ? "chip-text" : "chip-hue",
      needsConfirmation: false,
      suggested: null,
    };
  }

  // 2. Colour-narrowed pill re-read recovery (live's `colourNarrowedVariant`).
  //    Matched only against the page family's allowed tokens upstream, so it can
  //    never be an impossible variant. Present only when a page colour family
  //    was supplied to the OCR pass; absent on a bare upload.
  if (frame.colourNarrowedVariant != null) {
    return {
      variant: frame.colourNarrowedVariant,
      source: "colour-narrowed",
      needsConfirmation: false,
      suggested: null,
    };
  }

  // 3. Inconclusive. Do NOT inherit the viewed tab silently — require explicit
  //    confirmation, offering the viewed tab only as a suggestion.
  return {
    variant: null,
    source: "unresolved",
    needsConfirmation: true,
    suggested: suggestedVariant ?? null,
  };
}
